import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Link2, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { createShare, errMsg, listShares, revokeShare } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const EXPIRY = [[7, '7 days'], [30, '30 days'], [0, 'Never']];
const fullUrl = (path) => `${window.location.origin}${path}`;
const until = (s) => (s.expires_at ? `until ${new Date(s.expires_at).toLocaleDateString()}` : `no expiry · while the cut stays in your library (${new Date(s.effective_until).toLocaleDateString()})`);

async function copy(text) {
  try { await navigator.clipboard.writeText(text); toast.success('Link copied'); } catch { toast.message(text); }
}

/** Private no-login watch links for a finished cut — expiry 7 / 30 days / never, revocable, client can download. */
export function ShareCutDialog({ itemId, open, onOpenChange }) {
  const [days, setDays] = useState(7);
  const [shares, setShares] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => listShares(itemId).then((d) => setShares(d.shares || [])).catch((e) => toast.error(errMsg(e))), [itemId]);
  useEffect(() => { if (open && itemId) load(); }, [open, itemId, load]);
  const create = async () => {
    setBusy(true);
    try { const s = await createShare(itemId, days); await copy(fullUrl(s.path)); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revoke = async (token) => { try { await revokeShare(token); toast.success('Link turned off'); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-w-lg border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="share-cut-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 size={16} className="text-amber-300" /> Share with a client</DialogTitle>
          <DialogDescription className="text-neutral-400">A private link to watch and download this cut — no login needed. Turn it off any time.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-neutral-400">Link works for</span>
          {EXPIRY.map(([d, label]) => (
            <button key={d} type="button" onClick={() => setDays(d)} data-testid={`share-expiry-${d}`}
              className={cn('rounded-full border px-3 py-1 text-xs transition-colors duration-200', days === d ? 'border-amber-400 bg-amber-500/10 text-neutral-100' : 'border-neutral-800 text-neutral-400 hover:border-neutral-600')}>
              {label}
            </button>
          ))}
          <Button size="sm" onClick={create} disabled={busy} className="ml-auto bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="share-create-btn">
            {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Link2 size={13} className="mr-1" />} Create & copy link
          </Button>
        </div>
        <div className="space-y-2" data-testid="share-list">
          {shares.length === 0 && <div className="text-xs text-neutral-500" data-testid="share-empty">No active links yet.</div>}
          {shares.map((s) => (
            <div key={s.token} className="flex items-center gap-2 rounded-lg border border-neutral-800 p-2" data-testid={`share-row-${s.token}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-mono text-[11px] text-neutral-200">{fullUrl(s.path)}</div>
                <div className="text-[10px] text-neutral-500">{until(s)} · {s.views} views · {s.downloads} downloads</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copy(fullUrl(s.path))} className="text-neutral-300" data-testid="share-copy-btn"><Copy size={13} /></Button>
              <Button size="sm" variant="ghost" onClick={() => revoke(s.token)} className="text-neutral-400 hover:text-red-300" data-testid="share-revoke-btn"><Trash2 size={13} /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
